/* Application routes definition */

import * as Sentry from '@sentry/react';
import {Suspense, lazy} from 'react';
import {useSelector} from 'react-redux';
import {Route, Routes, Navigate} from 'react-router-dom';

import FeatureFlagsPanel from 'App/features/featureFlags/components/featureFlagsPanel';
import {selectUserCredentials} from 'App/features/user/userSlice';
import getConfig from '~/load_config';

import InflowSpinner from '../app/components/spinner/inflowSpinner';

import AppContainer from './pages/app/app_container';
import AutoAuth from './pages/app/auto_auth';

const InvalidUserPage = lazy(
  () => import('App/pages/invalidUserPage/invalidUserPage'),
);
const LandContainer = lazy(() => import('./pages/land/land_container'));
const AdminContainer = lazy(() => import('./pages/admin/admin_container'));
const AuthContainer = lazy(() => import('./pages/auth/auth_container'));
const AccountContainer = lazy(
  () => import('./pages/account/account_container'),
);
const HomeContainer = lazy(() => import('./pages/home/home_container'));
const ProfileContainer = lazy(
  () => import('./pages/profile/profile_container'),
);
const PageContainer = lazy(() => import('./pages/page/page_container'));
const ProjectGroupLoader = lazy(
  () => import('./../app/pages/project/projectGroupLoader'),
);
const CampaignsContainer = lazy(
  () => import('./pages/campaign/campaigns_container'),
);
const CampaignContainer = lazy(
  () => import('./pages/campaign/campaign_container'),
);
const CampaignStepperPage = lazy(
  () =>
    import('./../app/features/campaign/campaignStepper/campaignStepperPage'),
);
const ReleaseNotesContainer = lazy(
  () => import('./pages/release_notes/release_notes_container'),
);
const RulesEditorApp = lazy(() => import('../rules-editor/components/app'));
const NotFound = lazy(() => import('App/pages/notFound'));
const IndexContainer = lazy(() => import('./pages/index_container'));
const PagesContainer = lazy(() => import('./pages/page/pages_container'));
const HomePage = lazy(() => import('App/pages/homePage/homePage'));
const UIPage = lazy(() => import('~/ui/uiPage'));

function AuthenticatedRoutes(props) {
  const isDev = !getConfig().isProduction;

  return (
    <>
      <Suspense fallback={<InflowSpinner />}>
        <Routes>
          {['/projects', '/'].map(route => (
            <Route
              key={route}
              path={route}
              element={<HomeContainer {...props} />}
            />
          ))}
          <Route
            path="/projects/:projectHash/*"
            element={<ProjectGroupLoader {...props} />}
          />
          {['/acs', '/home'].map(route => (
            <Route key={route} path={route} element={<HomePage {...props} />} />
          ))}
          <Route path="/search" element={<IndexContainer {...props} />} />
          {[
            '/account/profile',
            '/account/authentication',
            '/account/address',
          ].map(route => (
            <Route
              key={route}
              path={route}
              element={<AccountContainer {...props} />}
            />
          ))}
          <Route path="/pages/:pageId" element={<PageContainer {...props} />} />
          <Route
            path="/profiles/:userId"
            element={<ProfileContainer {...props} />}
          />
          <Route path="/land" element={<LandContainer {...props} />} />
          <Route path="/admin" element={<AdminContainer {...props} />} />
          <Route path="/pages" element={<PagesContainer {...props} />} />
          <Route
            path="/campaigns"
            element={<CampaignsContainer {...props} />}
          />
          <Route
            path="/campaigns/:hash/*"
            element={<CampaignContainer {...props} />}
          />
          <Route
            path="/campaigns/creation/*"
            element={<CampaignStepperPage {...props} />}
          />
          <Route
            path="/release_notes"
            element={<ReleaseNotesContainer {...props} />}
          />
          {isDev && <Route path="/ui/*" element={<UIPage />} />}
          {['/login', '/signup', '/error'].map(route => (
            <Route path={route} key={route} element={<Navigate to="/" />} />
          ))}
          <Route path="*" element={<NotFound {...props} />} />
        </Routes>
      </Suspense>
    </>
  );
}

function AuthenticationRoutes() {
  return (
    <Routes>
      <Route path="*" element={<AuthContainer />} />
    </Routes>
  );
}

function InvalidUserRoutes(props) {
  return (
    <Suspense fallback={<InflowSpinner />}>
      <Routes>
        <Route path="*" element={<InvalidUserPage {...props} />} />
      </Routes>
    </Suspense>
  );
}

export function AppRoutes() {
  const credentials = useSelector(selectUserCredentials);

  if (credentials) {
    if (credentials.user.is_valid) {
      return (
        <AppContainer>
          <AuthenticatedRoutes />
        </AppContainer>
      );
    }
    return <InvalidUserRoutes />;
  }

  return <AuthenticationRoutes />;
}

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export default function RootRoutes() {
  return (
    <Suspense fallback={<InflowSpinner />}>
      <SentryRoutes>
        <Route
          path="*"
          element={
            <AutoAuth>
              <AppRoutes />
              <FeatureFlagsPanel />
            </AutoAuth>
          }
        />
        <Route
          path="/rules-editor"
          element={<Navigate to="/rules-editor/rules" />}
        />
        <Route path="/rules-editor/:page" element={<RulesEditorApp />} />
      </SentryRoutes>
    </Suspense>
  );
}
